import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Upload, Trash2, Plus, ChevronLeft, ChevronRight, Eye, Loader2, Sparkles } from 'lucide-react';
import { CTA_TEMPLATES, DISPLAY_MODES, FONTS, BUTTON_SIZES, ANIMATIONS, getDefaultCTAData } from './CTATemplates';
import CTARenderer from './CTARenderer';
import uploadService from '../../../services/uploadService';
import { toast } from 'react-hot-toast';

// Custom Slider component
const Slider = ({ value, onChange, min = 0, max = 100, step = 1 }) => {
  const handleChange = (e) => {
    onChange([parseInt(e.target.value)]);
  };

  return (
    <input
      type="range"
      value={value[0]}
      onChange={handleChange}
      min={min}
      max={max}
      step={step}
      className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-[#6a1fbf]"
    />
  );
};

// Custom Switch component
const Switch = ({ checked, onCheckedChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={!!checked}
    onClick={() => onCheckedChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
      checked ? 'bg-[#6a1fbf]' : 'bg-white/20'
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
        checked ? 'translate-x-5' : 'translate-x-0.5'
      }`}
    />
  </button>
);

// Custom Select component - uses portal to avoid dropdown cutoff from overflow containers
const Select = ({ value, onValueChange, children, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check both the select button AND the portal dropdown
      if (
        selectRef.current && !selectRef.current.contains(event.target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(event.target))
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && selectRef.current) {
      const rect = selectRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = 200;
      const openUpward = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

      setDropdownStyle({
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        zIndex: 99999,
        ...(openUpward
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
      });
    }
  }, [isOpen]);

  const options = React.Children.toArray(children).filter(
    child => child.type === SelectItem
  );

  const selectedOption = options.find(opt => opt.props.value === value);

  return (
    <div className="relative" ref={selectRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm hover:border-white/20 transition-colors"
      >
        <span className={selectedOption ? '' : 'text-gray-500'}>
          {selectedOption ? selectedOption.props.children : placeholder}
        </span>
        <ChevronRight className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
      </button>
      {isOpen && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={dropdownStyle}
          className="bg-[#1a1a2e] border border-white/10 rounded-lg shadow-xl py-1 max-h-60 overflow-y-auto"
        >
          {options.map((option) => (
            <button
              key={option.props.value}
              onClick={() => {
                onValueChange(option.props.value);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-white/10 transition-colors ${
                option.props.value === value ? 'text-[#6a1fbf]' : 'text-gray-300'
              }`}
            >
              {option.props.children}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

const SelectItem = ({ value, children }) => null;

// Custom Tabs component
const Tabs = ({ defaultValue, children, className = '' }) => {
  const [activeTab, setActiveTab] = useState(defaultValue);

  const tabsList = React.Children.toArray(children).find(
    child => child.type === TabsList
  );
  const tabsContents = React.Children.toArray(children).filter(
    child => child.type === TabsContent
  );

  return (
    <div className={className}>
      {React.cloneElement(tabsList, { activeTab, setActiveTab })}
      {tabsContents.map(content =>
        content.props.value === activeTab ? content : null
      )}
    </div>
  );
};

const TabsList = ({ children, activeTab, setActiveTab, className = '' }) => (
  <div className={`flex ${className}`}>
    {React.Children.map(children, child =>
      React.cloneElement(child, { activeTab, setActiveTab })
    )}
  </div>
);

const TabsTrigger = ({ value, children, activeTab, setActiveTab }) => (
  <button
    type="button"
    onClick={() => setActiveTab(value)}
    className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
      activeTab === value
        ? 'bg-[#6a1fbf] text-white'
        : 'text-gray-400 hover:text-white hover:bg-white/5'
    }`}
  >
    {children}
  </button>
);

const TabsContent = ({ value, children, className = '' }) => (
  <div className={className}>{children}</div>
);

export default function CTAModal({ isOpen, onClose, onSave, cta, folders, currentFolder, skipFolderSelect = false, readOnly = false, onCopyToMyElements }) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(getDefaultCTAData('offer'));
  const [showPreview, setShowPreview] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [hasInitialized, setHasInitialized] = useState(false);
  const fileInputRef = useRef(null);
  const bgFileInputRef = useRef(null);

  // Initialize form data only when modal opens, not on every currentFolder change
  useEffect(() => {
    if (isOpen && !hasInitialized) {
      if (cta) {
        setFormData({ ...getDefaultCTAData(cta.templateId || 'offer'), ...cta });
        setStep(2);
      } else {
        setFormData({ ...getDefaultCTAData('offer'), folder_id: currentFolder?.id });
        setStep(1);
      }
      setHasInitialized(true);
    }
  }, [isOpen, cta, currentFolder, hasInitialized]);

  // Reset initialization flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      setHasInitialized(false);
    }
  }, [isOpen]);

  const updateField = (path, value) => {
    const keys = path.split('.');
    setFormData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const handleSelectTemplate = (templateId) => {
    const defaults = getDefaultCTAData(templateId);
    setFormData(prev => ({
      ...defaults,
      folder_id: prev.folder_id,
      name: prev.name
    }));
    setStep(2);
  };

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  const addListItem = () => {
    updateField('listItems', [...(formData.listItems || []), '']);
  };

  const updateListItem = (index, value) => {
    const newItems = [...(formData.listItems || [])];
    newItems[index] = value;
    updateField('listItems', newItems);
  };

  const removeListItem = (index) => {
    const newItems = formData.listItems.filter((_, i) => i !== index);
    updateField('listItems', newItems);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingImage(true);
    try {
      const response = await uploadService.uploadFile(file);
      if (response?.url) {
        updateField('image.url', response.url);
        toast.success('Image uploaded');
      }
    } catch (error) {
      console.error('Failed to upload image:', error);
      toast.error('Failed to upload image');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleBgImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingImage(true);
    try {
      const response = await uploadService.uploadFile(file);
      if (response?.url) {
        updateField('background.imageUrl', response.url);
        toast.success('Background uploaded');
      }
    } catch (error) {
      console.error('Failed to upload background image:', error);
      toast.error('Failed to upload background');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!aiPrompt.trim()) return;

    setIsGeneratingImage(true);
    try {
      const baseUrl = process.env.REACT_APP_BASE_URL || '';
      const response = await fetch(`${baseUrl}/openai/generate-cta-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: aiPrompt }),
      });

      const data = await response.json();

      if (response.ok && data.imageUrl) {
        updateField('image.url', data.imageUrl);
        setAiPrompt('');
        toast.success('Image generated!');
      } else {
        toast.error(data.error || 'Failed to generate image');
      }
    } catch (error) {
      console.error('Failed to generate image:', error);
      toast.error('Failed to generate image');
    } finally {
      setIsGeneratingImage(false);
    }
  };

  if (!isOpen) return null;

  const selectedTemplate = CTA_TEMPLATES.find(t => t.id === formData.templateId);
  const selectedDisplayMode = DISPLAY_MODES.find(m => m.id === formData.displayMode);

  const modalContent = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[#001233] border border-white/10 rounded-2xl w-full max-w-6xl max-h-[90vh] flex flex-col shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10 flex-shrink-0">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-bold text-white">
                {cta ? 'Edit CTA Banner' : 'Create CTA Banner'}
              </h2>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <button onClick={() => setStep(1)} className={`hover:text-white transition-colors ${step >= 1 ? 'text-[#6a1fbf]' : ''}`}>Template</button>
                <ChevronRight className="w-4 h-4" />
                <button onClick={() => setStep(2)} className={`hover:text-white transition-colors ${step >= 2 ? 'text-[#6a1fbf]' : ''}`}>Content</button>
                <ChevronRight className="w-4 h-4" />
                <button onClick={() => setStep(3)} className={`hover:text-white transition-colors ${step >= 3 ? 'text-[#6a1fbf]' : ''}`}>Style</button>
                <ChevronRight className="w-4 h-4" />
                <button onClick={() => setStep(4)} className={`hover:text-white transition-colors ${step >= 4 ? 'text-[#6a1fbf]' : ''}`}>Display</button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm border border-white/20 rounded-lg text-gray-300 hover:bg-white/10 transition-colors"
              >
                <Eye className="w-4 h-4" />
                {showPreview ? 'Hide' : 'Show'} Preview
              </button>
              <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>

          <div className="flex flex-1 min-h-0 overflow-hidden">
            {/* Main Content */}
            <div className={`${showPreview ? 'w-1/2' : 'flex-1'} p-6 border-r border-white/10 overflow-y-auto custom-scroll`}>
              {/* Read-only banner */}
              {readOnly && (
                <div className="flex items-start gap-3 mb-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                  <p className="text-sm text-purple-200">
                    This element is from a shared library and is <strong>view only</strong>. Copy it to your library to make changes.
                  </p>
                </div>
              )}

              {/* Step 1: Template Selection */}
              {step === 1 && (
                <div className="space-y-6">
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Select Folder</label>
                    <Select value={formData.folder_id} onValueChange={(v) => updateField('folder_id', v)} placeholder="Choose folder...">
                      {folders?.map(folder => (
                        <SelectItem key={folder.id} value={folder.id}>{folder.name}</SelectItem>
                      ))}
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm text-gray-400 mb-3 block">Choose Template</label>
                    <div className="grid grid-cols-2 gap-3">
                      {CTA_TEMPLATES.map((template) => (
                        <button
                          key={template.id}
                          onClick={() => handleSelectTemplate(template.id)}
                          className={`p-4 rounded-xl text-left transition-all ${
                            formData.templateId === template.id
                              ? 'bg-[#6a1fbf] ring-2 ring-[#6a1fbf]'
                              : 'bg-white/5 hover:bg-white/10'
                          }`}
                        >
                          <div className="text-3xl mb-2">{template.icon}</div>
                          <p className="font-medium text-white text-sm">{template.name}</p>
                          <p className="text-xs text-gray-400">{template.description}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Content */}
              {step === 2 && (
                <div className="space-y-6">
                  {/* Folder Selection - Required (only if not skipping) */}
                  {!skipFolderSelect && (
                    <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                      <label className="text-white font-medium mb-2 block">Folder <span className="text-red-400">*</span></label>
                      <Select value={formData.folder_id || ''} onValueChange={(v) => updateField('folder_id', v)} placeholder="Select a folder...">
                        {folders?.map(folder => (
                          <SelectItem key={folder.id} value={folder.id}>{folder.name}</SelectItem>
                        ))}
                      </Select>
                      {!formData.folder_id && (
                        <p className="text-xs text-red-400 mt-1">Please select a folder to save this CTA</p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">CTA Name (internal)</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => updateField('name', e.target.value)}
                      disabled={readOnly}
                      placeholder="e.g., Holiday Sale CTA"
                      className={`w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-[#6a1fbf] transition-colors ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                    />
                  </div>

                  <Tabs defaultValue="text" className="w-full">
                    <TabsList className="grid grid-cols-4 w-full bg-white/5 rounded-lg overflow-hidden">
                      <TabsTrigger value="text">Text</TabsTrigger>
                      <TabsTrigger value="buttons">Buttons</TabsTrigger>
                      <TabsTrigger value="image">Image</TabsTrigger>
                      <TabsTrigger value="extras">Extras</TabsTrigger>
                    </TabsList>

                    <TabsContent value="text" className="space-y-4 mt-4">
                      {/* Headline */}
                      <div className="space-y-3 p-4 bg-white/5 rounded-xl">
                        <label className="text-white font-medium">Headline</label>
                        <input
                          type="text"
                          value={formData.headline?.text || ''}
                          onChange={(e) => updateField('headline.text', e.target.value)}
                          placeholder="Enter headline..."
                          className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-lg placeholder-gray-500 outline-none focus:border-[#6a1fbf]"
                        />
                        <div className="grid grid-cols-3 gap-3">
                          <Select value={formData.headline?.font} onValueChange={(v) => updateField('headline.font', v)} placeholder="Font">
                            {FONTS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                          </Select>
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => updateField('headline.weight', formData.headline?.weight === 'bold' ? 'normal' : 'bold')}
                              className={`flex-1 rounded-lg text-sm py-2 ${formData.headline?.weight === 'bold' ? 'bg-[#6a1fbf]' : 'bg-white/10'} text-white font-bold`}
                            >B</button>
                            <button
                              type="button"
                              onClick={() => updateField('headline.italic', !formData.headline?.italic)}
                              className={`flex-1 rounded-lg text-sm italic py-2 ${formData.headline?.italic ? 'bg-[#6a1fbf]' : 'bg-white/10'} text-white`}
                            >I</button>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={formData.headline?.color || '#ffffff'}
                              onChange={(e) => updateField('headline.color', e.target.value)}
                              className="w-8 h-8 rounded cursor-pointer bg-transparent"
                            />
                            <span className="text-xs text-gray-400">Color</span>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-gray-400">Size: {formData.headline?.size}px</label>
                          <Slider
                            value={[formData.headline?.size || 28]}
                            onChange={([v]) => updateField('headline.size', v)}
                            min={18} max={72} step={2}
                          />
                        </div>
                        <div className="flex gap-2">
                          {['left', 'center', 'right'].map(align => (
                            <button
                              key={align}
                              type="button"
                              onClick={() => updateField('headline.alignment', align)}
                              className={`flex-1 py-2 rounded-lg text-xs capitalize ${
                                formData.headline?.alignment === align ? 'bg-[#6a1fbf]' : 'bg-white/10'
                              } text-white`}
                            >{align}</button>
                          ))}
                        </div>
                      </div>

                      {/* Tagline */}
                      <div className="space-y-3 p-4 bg-white/5 rounded-xl">
                        <label className="text-white font-medium">Tagline</label>
                        <textarea
                          value={formData.tagline?.text || ''}
                          onChange={(e) => updateField('tagline.text', e.target.value)}
                          placeholder="Enter tagline..."
                          className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-[#6a1fbf] resize-none"
                          rows={2}
                        />
                        <div className="grid grid-cols-3 gap-3">
                          <Select value={formData.tagline?.font} onValueChange={(v) => updateField('tagline.font', v)} placeholder="Font">
                            {FONTS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                          </Select>
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => updateField('tagline.weight', formData.tagline?.weight === 'bold' ? 'normal' : 'bold')}
                              className={`flex-1 rounded-lg text-sm ${formData.tagline?.weight === 'bold' ? 'bg-[#6a1fbf]' : 'bg-white/10'} text-white font-bold`}
                            >B</button>
                            <button
                              type="button"
                              onClick={() => updateField('tagline.italic', !formData.tagline?.italic)}
                              className={`flex-1 rounded-lg text-sm italic ${formData.tagline?.italic ? 'bg-[#6a1fbf]' : 'bg-white/10'} text-white`}
                            >I</button>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={formData.tagline?.color || '#ffffffcc'}
                              onChange={(e) => updateField('tagline.color', e.target.value)}
                              className="w-8 h-8 rounded cursor-pointer bg-transparent"
                            />
                            <span className="text-xs text-gray-400">Color</span>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-gray-400">Size: {formData.tagline?.size || 16}px</label>
                          <Slider
                            value={[formData.tagline?.size || 16]}
                            onChange={([v]) => updateField('tagline.size', v)}
                            min={10} max={48} step={1}
                          />
                        </div>
                        <div className="flex gap-2">
                          {['left', 'center', 'right'].map(align => (
                            <button
                              key={align}
                              type="button"
                              onClick={() => updateField('tagline.alignment', align)}
                              className={`flex-1 py-2 rounded-lg text-xs capitalize ${
                                (formData.tagline?.alignment || 'center') === align ? 'bg-[#6a1fbf]' : 'bg-white/10'
                              } text-white`}
                            >{align}</button>
                          ))}
                        </div>
                      </div>

                      {/* Body Text */}
                      <div className="space-y-3 p-4 bg-white/5 rounded-xl">
                        <label className="text-white font-medium">Body Text (optional)</label>
                        <textarea
                          value={formData.bodyText?.text || ''}
                          onChange={(e) => updateField('bodyText.text', e.target.value)}
                          placeholder="Additional description..."
                          className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-[#6a1fbf] resize-none"
                          rows={3}
                        />
                        <div>
                          <label className="text-xs text-gray-400">Size: {formData.bodyText?.size || 14}px</label>
                          <Slider
                            value={[formData.bodyText?.size || 14]}
                            onChange={([v]) => updateField('bodyText.size', v)}
                            min={10} max={48} step={1}
                          />
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="buttons" className="space-y-4 mt-4">
                      {/* Primary Button */}
                      <div className="space-y-3 p-4 bg-white/5 rounded-xl">
                        <label className="text-white font-medium">Primary Button</label>

                        {/* Button Vertical Position */}
                        <div>
                          <label className="text-xs text-gray-400 mb-2 block">Vertical Position</label>
                          <div className="grid grid-cols-3 gap-2">
                            {['top', 'middle', 'bottom'].map(pos => (
                              <button
                                key={pos}
                                type="button"
                                onClick={() => updateField('buttons.0.verticalPosition', pos)}
                                className={`p-2 rounded-lg text-xs capitalize ${
                                  (formData.buttons?.[0]?.verticalPosition || 'middle') === pos ? 'bg-[#6a1fbf]' : 'bg-white/10'
                                } text-white`}
                              >{pos}</button>
                            ))}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <input
                            type="text"
                            value={formData.buttons?.[0]?.text || ''}
                            onChange={(e) => updateField('buttons.0.text', e.target.value)}
                            placeholder="Button text..."
                            className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-[#6a1fbf]"
                          />
                          <input
                            type="text"
                            value={formData.buttons?.[0]?.url || ''}
                            onChange={(e) => updateField('buttons.0.url', e.target.value)}
                            placeholder="https://..."
                            className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-[#6a1fbf]"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <Select value={formData.buttons?.[0]?.size || 'md'} onValueChange={(v) => updateField('buttons.0.size', v)} placeholder="Size">
                            {BUTTON_SIZES.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                          </Select>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={formData.buttons?.[0]?.bgColor || '#00c853'}
                              onChange={(e) => updateField('buttons.0.bgColor', e.target.value)}
                              className="w-8 h-8 rounded cursor-pointer bg-transparent"
                            />
                            <span className="text-xs text-gray-400">BG</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={formData.buttons?.[0]?.textColor || '#ffffff'}
                              onChange={(e) => updateField('buttons.0.textColor', e.target.value)}
                              className="w-8 h-8 rounded cursor-pointer bg-transparent"
                            />
                            <span className="text-xs text-gray-400">Text</span>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-gray-400">Border Radius: {formData.buttons?.[0]?.borderRadius || 8}px</label>
                          <Slider
                            value={[formData.buttons?.[0]?.borderRadius || 8]}
                            onChange={([v]) => updateField('buttons.0.borderRadius', v)}
                            min={0} max={50}
                          />
                        </div>
                        <div className="flex items-center gap-4">
                          <Switch
                            checked={formData.buttons?.[0]?.useGradient}
                            onCheckedChange={(v) => updateField('buttons.0.useGradient', v)}
                          />
                          <label className="text-xs text-gray-400">Use Gradient</label>
                          {formData.buttons?.[0]?.useGradient && (
                            <div className="flex items-center gap-2">
                              <input
                                type="color"
                                value={formData.buttons?.[0]?.gradientColor2 || '#6a1fbf'}
                                onChange={(e) => updateField('buttons.0.gradientColor2', e.target.value)}
                                className="w-8 h-8 rounded cursor-pointer bg-transparent"
                              />
                              <span className="text-xs text-gray-400">End Color</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Secondary Button */}
                      <div className="space-y-3 p-4 bg-white/5 rounded-xl">
                        <div className="flex items-center justify-between">
                          <label className="text-white font-medium">Secondary Button</label>
                          <Switch
                            checked={formData.secondaryButton?.enabled}
                            onCheckedChange={(v) => updateField('secondaryButton.enabled', v)}
                          />
                        </div>
                        {formData.secondaryButton?.enabled && (
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <input
                                type="text"
                                value={formData.secondaryButton?.text || ''}
                                onChange={(e) => updateField('secondaryButton.text', e.target.value)}
                                placeholder="Button text..."
                                className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-[#6a1fbf]"
                              />
                              <input
                                type="text"
                                value={formData.secondaryButton?.url || ''}
                                onChange={(e) => updateField('secondaryButton.url', e.target.value)}
                                placeholder="https://..."
                                className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-[#6a1fbf]"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="image" className="space-y-4 mt-4">
                      <div className="space-y-4 p-4 bg-white/5 rounded-xl">
                        <label className="text-white font-medium">Spotlight Image</label>

                        {/* Current Image Preview */}
                        {formData.image?.url && (
                          <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                            <img src={formData.image.url} alt="" className="w-20 h-20 object-cover rounded-lg" />
                            <div className="flex-1">
                              <p className="text-sm text-gray-300 truncate">{formData.image.url.split('/').pop()}</p>
                              <p className="text-xs text-gray-500">Current image</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => updateField('image.url', '')}
                              className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}

                        {/* Upload Section */}
                        {!readOnly && (
                          <div>
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept="image/*"
                              onChange={handleImageUpload}
                              className="hidden"
                            />
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={isUploadingImage}
                              className="w-full border-2 border-dashed border-white/20 rounded-xl p-6 text-center hover:border-[#6a1fbf]/50 transition-colors cursor-pointer disabled:opacity-50"
                            >
                              {isUploadingImage ? (
                                <Loader2 className="w-8 h-8 text-[#6a1fbf] mx-auto mb-2 animate-spin" />
                              ) : (
                                <Upload className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                              )}
                              <p className="text-sm text-gray-400">
                                {isUploadingImage ? 'Uploading...' : 'Click to upload image'}
                              </p>
                              <p className="text-xs text-gray-500">PNG, JPG up to 5MB</p>
                            </button>
                          </div>
                        )}

                        {/* AI Image Generator */}
                        <div className="space-y-3 p-4 bg-gradient-to-br from-[#6a1fbf]/10 to-[#00c853]/10 rounded-xl border border-[#6a1fbf]/20">
                          <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-[#6a1fbf]" />
                            <label className="text-white font-medium">Generate with AI</label>
                          </div>
                          <textarea
                            value={aiPrompt}
                            onChange={(e) => setAiPrompt(e.target.value)}
                            placeholder="Describe the image you want to generate..."
                            className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-[#6a1fbf] resize-none"
                            rows={2}
                          />
                          <button
                            type="button"
                            onClick={handleGenerateImage}
                            disabled={isGeneratingImage || !aiPrompt.trim()}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:from-[#7a2fcf] hover:to-[#00d863] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isGeneratingImage ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <Sparkles className="w-4 h-4" />
                                Generate Image
                              </>
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Image Settings */}
                      {formData.image?.url && (
                        <div className="space-y-3 p-4 bg-white/5 rounded-xl">
                          <label className="text-white font-medium">Image Settings</label>
                          <div className="grid grid-cols-2 gap-3">
                            <Select value={formData.image?.position || 'top'} onValueChange={(v) => updateField('image.position', v)} placeholder="Position">
                              <SelectItem value="top">Top</SelectItem>
                              <SelectItem value="bottom">Bottom</SelectItem>
                              <SelectItem value="left">Left</SelectItem>
                              <SelectItem value="right">Right</SelectItem>
                            </Select>
                            <Select value={formData.image?.size || 'medium'} onValueChange={(v) => updateField('image.size', v)} placeholder="Size">
                              <SelectItem value="small">Small</SelectItem>
                              <SelectItem value="medium">Medium</SelectItem>
                              <SelectItem value="large">Large</SelectItem>
                            </Select>
                          </div>
                        </div>
                      )}
                    </TabsContent>

                    <TabsContent value="extras" className="space-y-4 mt-4">
                      {/* Pricing */}
                      <div className="space-y-3 p-4 bg-white/5 rounded-xl">
                        <div className="flex items-center justify-between">
                          <label className="text-white font-medium">Show Pricing</label>
                          <Switch
                            checked={formData.pricing?.enabled}
                            onCheckedChange={(v) => updateField('pricing.enabled', v)}
                          />
                        </div>
                        {formData.pricing?.enabled && (
                          <div className="grid grid-cols-3 gap-3">
                            <input
                              type="text"
                              value={formData.pricing?.price || ''}
                              onChange={(e) => updateField('pricing.price', e.target.value)}
                              placeholder="99"
                              className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-[#6a1fbf]"
                            />
                            <input
                              type="text"
                              value={formData.pricing?.originalPrice || ''}
                              onChange={(e) => updateField('pricing.originalPrice', e.target.value)}
                              placeholder="199 (crossed)"
                              className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-[#6a1fbf]"
                            />
                            <Select value={formData.pricing?.currency || '$'} onValueChange={(v) => updateField('pricing.currency', v)}>
                              <SelectItem value="$">$</SelectItem>
                              <SelectItem value="€">€</SelectItem>
                              <SelectItem value="£">£</SelectItem>
                            </Select>
                          </div>
                        )}
                      </div>

                      {/* List Items */}
                      <div className="space-y-3 p-4 bg-white/5 rounded-xl">
                        <label className="text-white font-medium">Value Stack / List Items</label>
                        {formData.listItems?.map((item, index) => (
                          <div key={index} className="flex gap-2">
                            <input
                              type="text"
                              value={item}
                              onChange={(e) => updateListItem(index, e.target.value)}
                              placeholder={`Item ${index + 1}...`}
                              className="flex-1 px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-[#6a1fbf]"
                            />
                            <button
                              type="button"
                              onClick={() => removeListItem(index)}
                              className="p-2.5 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={addListItem}
                          className="w-full flex items-center justify-center gap-2 py-2.5 border border-dashed border-white/20 rounded-lg text-gray-400 hover:border-white/40 hover:text-white transition-colors"
                        >
                          <Plus className="w-4 h-4" /> Add Item
                        </button>
                      </div>

                      {/* Event Details */}
                      <div className="space-y-3 p-4 bg-white/5 rounded-xl">
                        <div className="flex items-center justify-between">
                          <label className="text-white font-medium">Event Details</label>
                          <Switch
                            checked={formData.eventDetails?.enabled}
                            onCheckedChange={(v) => updateField('eventDetails.enabled', v)}
                          />
                        </div>
                        {formData.eventDetails?.enabled && (
                          <div className="grid grid-cols-2 gap-3">
                            <input
                              type="text"
                              value={formData.eventDetails?.date || ''}
                              onChange={(e) => updateField('eventDetails.date', e.target.value)}
                              placeholder="December 15, 2024"
                              className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-[#6a1fbf]"
                            />
                            <input
                              type="text"
                              value={formData.eventDetails?.time || ''}
                              onChange={(e) => updateField('eventDetails.time', e.target.value)}
                              placeholder="2:00 PM EST"
                              className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 outline-none focus:border-[#6a1fbf]"
                            />
                          </div>
                        )}
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              )}

              {/* Step 3: Style */}
              {step === 3 && (
                <div className="space-y-6">
                  {/* Background */}
                  <div className="space-y-3 p-4 bg-white/5 rounded-xl">
                    <label className="text-white font-medium">Background</label>
                    <div className="grid grid-cols-3 gap-2">
                      {['solid', 'gradient', 'image'].map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => updateField('background.type', type)}
                          className={`p-3 rounded-xl capitalize text-sm ${
                            formData.background?.type === type ? 'bg-[#6a1fbf]' : 'bg-white/10'
                          } text-white`}
                        >{type}</button>
                      ))}
                    </div>

                    {formData.background?.type === 'solid' && (
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={formData.background?.color || '#6a1fbf'}
                          onChange={(e) => updateField('background.color', e.target.value)}
                          className="w-12 h-12 rounded cursor-pointer bg-transparent"
                        />
                        <input
                          type="text"
                          value={formData.background?.color || '#6a1fbf'}
                          onChange={(e) => updateField('background.color', e.target.value)}
                          className="flex-1 px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white outline-none focus:border-[#6a1fbf]"
                        />
                      </div>
                    )}

                    {formData.background?.type === 'gradient' && (
                      <div className="space-y-3">
                        <div className="flex gap-4">
                          <div className="flex-1">
                            <label className="text-xs text-gray-400 mb-1 block">Start Color</label>
                            <input
                              type="color"
                              value={formData.background?.color || '#6a1fbf'}
                              onChange={(e) => updateField('background.color', e.target.value)}
                              className="w-full h-10 rounded cursor-pointer bg-transparent"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="text-xs text-gray-400 mb-1 block">End Color</label>
                            <input
                              type="color"
                              value={formData.background?.gradientColor2 || '#001233'}
                              onChange={(e) => updateField('background.gradientColor2', e.target.value)}
                              className="w-full h-10 rounded cursor-pointer bg-transparent"
                            />
                          </div>
                        </div>
                        <div
                          className="h-12 rounded-xl"
                          style={{
                            background: `linear-gradient(${formData.background?.gradientDirection || '135deg'}, ${formData.background?.color}, ${formData.background?.gradientColor2})`
                          }}
                        />
                      </div>
                    )}

                    {formData.background?.type === 'image' && (
                      <div className="space-y-3">
                        <input
                          ref={bgFileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleBgImageUpload}
                          className="hidden"
                        />

                        {formData.background?.imageUrl && (
                          <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                            <img src={formData.background.imageUrl} alt="" className="w-20 h-20 object-cover rounded-lg" />
                            <div className="flex-1">
                              <p className="text-sm text-gray-300 truncate">{formData.background.imageUrl.split('/').pop()}</p>
                              <p className="text-xs text-gray-500">Background image</p>
                            </div>
                            {!readOnly && (
                              <button
                                type="button"
                                onClick={() => updateField('background.imageUrl', '')}
                                className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        )}

                        {!readOnly && (
                          <button
                            type="button"
                            onClick={() => bgFileInputRef.current?.click()}
                            disabled={isUploadingImage}
                            className="w-full border-2 border-dashed border-white/20 rounded-xl p-6 text-center hover:border-[#6a1fbf]/50 transition-colors cursor-pointer disabled:opacity-50"
                          >
                            {isUploadingImage ? (
                              <Loader2 className="w-8 h-8 text-[#6a1fbf] mx-auto mb-2 animate-spin" />
                            ) : (
                              <Upload className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                            )}
                            <p className="text-sm text-gray-400">
                              {isUploadingImage ? 'Uploading...' : 'Upload background image'}
                            </p>
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Padding */}
                  <div className="space-y-3 p-4 bg-white/5 rounded-xl">
                    <label className="text-white font-medium">Padding</label>
                    <div className="grid grid-cols-4 gap-3">
                      {['top', 'right', 'bottom', 'left'].map((side) => (
                        <div key={side}>
                          <label className="text-xs text-gray-500 mb-1 block capitalize">{side}</label>
                          <input
                            type="number"
                            value={formData.padding?.[side] || 24}
                            onChange={(e) => updateField(`padding.${side}`, parseInt(e.target.value))}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white outline-none focus:border-[#6a1fbf]"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Max Width (for centered) */}
                  <div className="space-y-3 p-4 bg-white/5 rounded-xl">
                    <label className="text-white font-medium">Max Width (Centered Mode)</label>
                    <div>
                      <label className="text-xs text-gray-400">{formData.maxWidth || 800}px</label>
                      <Slider
                        value={[formData.maxWidth || 800]}
                        onChange={([v]) => updateField('maxWidth', v)}
                        min={400} max={1200} step={50}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Display */}
              {step === 4 && (
                <div className="space-y-6">
                  {/* Display Mode */}
                  <div className="space-y-3 p-4 bg-white/5 rounded-xl">
                    <label className="text-white font-medium">Display Mode</label>
                    <div className="grid grid-cols-3 gap-3">
                      {DISPLAY_MODES.map((mode) => (
                        <button
                          key={mode.id}
                          type="button"
                          onClick={() => {
                            updateField('displayMode', mode.id);
                            updateField('position', mode.positions[0]);
                          }}
                          className={`p-4 rounded-xl text-center transition-all ${
                            formData.displayMode === mode.id
                              ? 'bg-[#6a1fbf] ring-2 ring-[#6a1fbf]'
                              : 'bg-white/10 hover:bg-white/20'
                          }`}
                        >
                          <div className="text-2xl mb-2">{mode.icon}</div>
                          <p className="text-white text-sm font-medium">{mode.name}</p>
                          <p className="text-xs text-gray-400">{mode.description}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Position */}
                  {selectedDisplayMode && (
                    <div className="space-y-3 p-4 bg-white/5 rounded-xl">
                      <label className="text-white font-medium">Position</label>
                      <div className="grid grid-cols-2 gap-3">
                        {selectedDisplayMode.positions.map((pos) => (
                          <button
                            key={pos}
                            type="button"
                            onClick={() => updateField('position', pos)}
                            className={`p-3 rounded-xl capitalize ${
                              formData.position === pos ? 'bg-[#6a1fbf]' : 'bg-white/10'
                            } text-white text-sm`}
                          >{pos}</button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Animation */}
                  <div className="space-y-3 p-4 bg-white/5 rounded-xl">
                    <label className="text-white font-medium">Animation</label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Entry</label>
                        <Select value={formData.animation?.entry || 'fade'} onValueChange={(v) => updateField('animation.entry', v)}>
                          {ANIMATIONS.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Exit</label>
                        <Select value={formData.animation?.exit || 'fade'} onValueChange={(v) => updateField('animation.exit', v)}>
                          {ANIMATIONS.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Backdrop Blur */}
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                    <div>
                      <p className="text-white font-medium">Backdrop Blur</p>
                      <p className="text-xs text-gray-400">Add blur effect behind centered CTAs</p>
                    </div>
                    <Switch
                      checked={formData.backdropBlur}
                      onCheckedChange={(v) => updateField('backdropBlur', v)}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Live Preview Panel */}
            {showPreview && (
              <div className="w-1/2 p-4 bg-black/30">
                <label className="text-gray-400 mb-3 block">Live Preview ({formData.displayMode})</label>
                <div className="relative bg-gray-900 rounded-xl overflow-hidden" style={{ height: 'calc(100% - 32px)' }}>
                  {/* Mock video area */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-32 h-24 bg-gray-800 rounded flex items-center justify-center">
                      <span className="text-xs text-gray-500">Video</span>
                    </div>
                  </div>

                  {/* CTA Preview based on display mode */}
                  {formData.displayMode === 'vertical' && (
                    <div
                      className={`absolute top-0 ${formData.position === 'left' ? 'left-0' : 'right-0'} h-full`}
                      style={{ width: '35%', minWidth: 120, maxWidth: 200 }}
                    >
                      <CTARenderer cta={formData} displayMode="vertical" className="w-full h-full" />
                    </div>
                  )}

                  {formData.displayMode === 'horizontal' && (
                    <div
                      className={`absolute ${formData.position === 'top' ? 'top-0' : 'bottom-0'} left-0 right-0`}
                      style={{ height: '30%', minHeight: 80, maxHeight: 150 }}
                    >
                      <CTARenderer cta={formData} displayMode="horizontal" className="w-full h-full" />
                    </div>
                  )}

                  {formData.displayMode === 'centered' && (
                    <div className="absolute inset-0 flex items-center justify-center p-4">
                      <div
                        className={`${formData.backdropBlur ? 'backdrop-blur-sm' : ''} rounded-xl overflow-hidden`}
                        style={{ width: '80%', maxWidth: formData.maxWidth || 400, maxHeight: '80%' }}
                      >
                        <CTARenderer cta={formData} displayMode="centered" className="w-full h-full min-h-[200px]" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t border-white/10 flex-shrink-0">
            <button
              type="button"
              onClick={() => step > 1 ? setStep(step - 1) : onClose()}
              className="flex items-center gap-2 px-4 py-2 text-sm border border-white/20 rounded-lg text-gray-300 hover:bg-white/10 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              {step > 1 ? 'Back' : 'Cancel'}
            </button>
            <div className="flex gap-2">
              {[1, 2, 3, 4].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStep(s)}
                  className={`w-8 h-2 rounded-full transition-colors ${s === step ? 'bg-[#6a1fbf]' : 'bg-white/20 hover:bg-white/30'}`}
                />
              ))}
            </div>
            {step < 4 ? (
              <button
                type="button"
                onClick={() => setStep(step + 1)}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:from-[#7a2fcf] hover:to-[#00d863] transition-all"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : readOnly ? (onCopyToMyElements && (
              <button
                type="button"
                onClick={onCopyToMyElements}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:opacity-90 transition-all"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                Copy to My Elements
              </button>
            )) : (
              <button
                type="button"
                onClick={handleSave}
                disabled={!skipFolderSelect && !formData.folder_id}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:from-[#7a2fcf] hover:to-[#00d863] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check className="w-4 h-4" />
                {cta ? 'Save Changes' : 'Create CTA'}
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  // Use portal to render at document root level
  return ReactDOM.createPortal(modalContent, document.body);
}
